// Phase 5 (web revival) — entry point.
//
// On boot, blind-fire a refresh against the HttpOnly cookie BEFORE the
// router mounts. If the cookie is valid we land directly on /library;
// if not, the router's auth gate kicks us to /login. This avoids the
// "login form flashes for 200ms after F5" footgun.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './app/router';
import { queryClient } from './app/queryClient';
import { api } from './api/client';
import { setSession } from './app/auth';
import { connect as connectWS } from './app/wsClient';
import type { AuthEnvelope } from './api/types';
// Fraunces variable (wght axis + italic) — display serif for wordmark,
// page titles, and month headers. See styles/index.css for the rationale.
import '@fontsource-variable/fraunces/index.css';
import '@fontsource-variable/fraunces/wght-italic.css';
import './styles/index.css';

async function bootstrap() {
  // Try the cookie refresh first. Failure is the common case (fresh
  // browser, expired cookie) — swallow it and let the auth gate route
  // to /login.
  try {
    const env = await fetch('/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'include',
    });
    if (env.ok) {
      const payload = (await env.json()) as AuthEnvelope;
      setSession(payload);
      void connectWS();
    }
  } catch {
    // network down, server down, no cookie — fall through to /login
  }

  // Whatever the cookie said, mount the app.
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root missing in index.html');
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );

  // Silence the unused-import lint for `api` — kept in scope so the
  // dev console has `window.dvphoto.api` to poke endpoints by hand.
  (window as unknown as { dvphoto?: unknown }).dvphoto = { api };
}

void bootstrap();
